import type { BalanceDto, LotStatus, MovementDto } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { TracePanel } from './TracePanel';
import { api } from '../lib/api';
import {
  formatDate,
  formatDateTime,
  formatQuantity,
  formatSignedQuantity,
  formatUnitCost,
  humanise,
} from '../lib/format';
import { Badge, Button, Card, CardBody, Spinner } from './ui';
import { DataTable, type DataColumn } from './DataTable';

const STATUS_TONES: Record<LotStatus, 'green' | 'hazard' | 'accent' | 'muted'> = {
  available: 'green',
  quarantine: 'hazard',
  rejected: 'muted',
  consumed: 'muted',
  scrapped: 'muted',
};

const BALANCE_COLUMNS: DataColumn<BalanceDto>[] = [
  {
    key: 'location',
    header: 'Location',
    primary: true,
    cell: (balance) => `${balance.locationCode} — ${balance.locationName}`,
  },
  {
    key: 'onHand',
    header: 'On hand',
    align: 'right',
    cell: (balance) => formatQuantity(balance.quantity, balance.baseUom),
  },
  { key: 'value', header: 'Value', cell: (balance) => formatQuantity(balance.value) },
];

const MOVEMENT_COLUMNS: DataColumn<MovementDto>[] = [
  {
    key: 'when',
    header: 'When',
    cell: (movement) => (
      <span className="whitespace-nowrap text-xs">{formatDateTime(movement.occurredAt)}</span>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    primary: true,
    cell: (movement) => (
      <>
        {humanise(movement.type)}
        {movement.reversalOfId ? (
          <span className="ml-1 text-xs text-accent">(reversal)</span>
        ) : null}
      </>
    ),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    align: 'right',
    cell: (movement) => (
      <span className={movement.direction === 'out' ? 'text-red-700' : 'text-brand-deep'}>
        {formatSignedQuantity(movement.quantity, movement.uom)}
        {movement.enteredUom && movement.enteredUom !== movement.uom ? (
          <span className="ml-1 text-xs text-ink-muted">
            ({formatQuantity(movement.enteredQuantity, movement.enteredUom)})
          </span>
        ) : null}
      </span>
    ),
  },
  { key: 'location', header: 'Location', cell: (movement) => movement.locationCode },
  {
    key: 'reason',
    header: 'Reason',
    cell: (movement) => <span className="text-xs">{movement.reason ?? '—'}</span>,
  },
  {
    key: 'by',
    header: 'By',
    cell: (movement) => <span className="text-xs">{movement.actorName ?? 'system'}</span>,
  },
];

/** Everything known about one lot: where it is, what it cost, and its history. */
export function LotDetailPanel({ lotId, onClose }: { lotId: string; onClose: () => void }) {
  const [tracing, setTracing] = useState(false);

  const detail = useQuery({
    queryKey: ['lot', lotId],
    queryFn: () => api.inventory.lot(lotId),
  });

  if (detail.isPending) {
    return (
      <Card className="mb-6">
        <Spinner label="Loading lot…" />
      </Card>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Card className="mb-6">
        <CardBody>
          <p className="text-sm text-ink-body">Could not load that lot.</p>
          <div className="mt-3">
            <Button tone="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const { lot, balances, movements } = detail.data;

  return (
    <>
      {tracing ? (
        <TracePanel start={{ kind: 'lot', id: lotId }} onClose={() => setTracing(false)} />
      ) : null}
      <Card className="mb-6 border-l-4 border-l-brand">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-deep">
              Lot {lot.code}
            </p>
            <h2 className="mt-1 text-lg font-bold text-ink">
              {lot.itemCode} — {lot.itemName}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONES[lot.status]}>{humanise(lot.status)}</Badge>
              <Badge tone="muted">{humanise(lot.origin)}</Badge>
              {lot.depleted ? <Badge tone="muted">Depleted</Badge> : null}
            </div>
          </div>
          <Button tone="ghost" onClick={() => setTracing((current) => !current)}>
            {tracing ? 'Hide trace' : 'Trace this lot'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Close
          </Button>
        </div>

        <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label="On hand" value={formatQuantity(lot.onHand, lot.baseUom)} />
          <Field label="Unit cost" value={formatUnitCost(lot.unitCost, lot.baseUom)} />
          <Field label="Expires" value={formatDate(lot.expiryDate)} />
          <Field label="Received" value={formatDateTime(lot.receivedAt)} />
          <Field label="Supplier" value={lot.supplierName ?? '—'} />
          <Field
            label="Version"
            value={`${lot.version}`}
          />
          <Field label="Item" value={lot.itemCode} />
          <Field label="Value on hand" value={formatQuantity(lot.value)} />
        </dl>

        <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
          Balances by location
        </h3>
        <div className="mt-2">
          <DataTable
            rows={balances}
            columns={BALANCE_COLUMNS}
            rowKey={(balance) => balance.locationId}
            empty="No balances — the lot has never been received."
          />
        </div>

        <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
          Ledger history ({movements.length})
        </h3>
        <div className="mt-2">
          <DataTable
            rows={movements}
            columns={MOVEMENT_COLUMNS}
            rowKey={(movement) => movement.id}
            rowClassName={(movement) => (movement.reversalOfId ? 'bg-accent-soft/40' : '')}
            empty="No movements yet."
          />
        </div>
      </CardBody>
    </Card>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-ink-body">{value}</dd>
    </div>
  );
}
